/**
 * The printed cloth: the betting circles and the house's legends, generated from the
 * rules object rather than copied off a photograph.
 *
 * ============================== WHY FROM THE RULES ==============================
 * A felt that says BLACKJACK PAYS 3 TO 2 is a promise, and the table keeps it only
 * because `rules.blackjackPays` is 1.5. The old print (`components/FeltPrint.tsx`) typed
 * the three legends in; this one reads them off the same object the engine settles
 * with, so a table dealt 6:5 would print 6 TO 5 and a dealer who hit soft 17 would say
 * so. `tests/blackjack-felt.test.ts` holds the words to the rules.
 *
 * Drawn in plate coordinates — the mock's 1100 x 619 box, `preserveAspectRatio="none"`
 * over a box that has the plate's own aspect — so the circles land where
 * `lib/blackjack-geometry.ts` says the spots are at every size, with the arcs and sizes
 * build-topdown.py drew them at.
 * ===============================================================================
 */
import type { BlackjackRules } from "@engine/blackjack/rules";
import { paysWords, printWords } from "./blackjack-view";
import { PLATE_ASPECT, PRINT, SPOT_K, CIRCLE_R, arc, arcPath, spotAngles } from "../lib/blackjack-geometry";

const W = 1100;
const H = W / PLATE_ASPECT;

/* The words themselves live in `blackjack-view.ts` with the rest of the table's words,
   so a Node test can read them without a JSX compiler; re-exported here for callers. */
export { paysWords, printWords };

export function BlackjackPrint({ rules, seats }: { rules: BlackjackRules; seats: number }) {
  const w = printWords(rules);
  return (
    <svg className="bk-print" viewBox={`0 0 ${W} ${H.toFixed(1)}`} preserveAspectRatio="none"
         aria-hidden="true" focusable="false">
      <defs>
        <path id="bkArcIns" d={arcPath(PRINT.insuranceK, PRINT.from, PRINT.to, W)} fill="none" />
        <path id="bkArcPay" d={arcPath(PRINT.paysK, PRINT.from, PRINT.to, W)} fill="none" />
        <path id="bkArcRule" d={arcPath(PRINT.ruleK, PRINT.ruleFrom, PRINT.ruleTo, W)} fill="none" />
      </defs>
      {w.insurance && (
        <text className="bk-print-ins" style={{ fontSize: PRINT.insuranceSize * W }}>
          <textPath href="#bkArcIns" startOffset="50%" textAnchor="middle">{w.insurance}</textPath>
        </text>
      )}
      <text className="bk-print-pays" style={{ fontSize: PRINT.paysSize * W }}>
        <textPath href="#bkArcPay" startOffset="50%" textAnchor="middle">{w.pays}</textPath>
      </text>
      {spotAngles(seats).map((deg) => {
        const [x, y] = arc(deg, SPOT_K);
        return <circle key={deg} className="bk-print-spot" cx={x * W} cy={y * H} r={CIRCLE_R * W} />;
      })}
      <text className="bk-print-small" style={{ fontSize: PRINT.ruleSize * W }}>
        <textPath href="#bkArcRule" startOffset="50%" textAnchor="middle">{w.dealer}</textPath>
      </text>
    </svg>
  );
}
